/**
 * Iter 112 · Admin CPanel · 💰 Πληρωμές tab.
 *
 * Πλήρες Payments dashboard με 5 στατιστικές κάρτες + πίνακα με φίλτρα +
 * approve / reject actions για pending_admin payments. Auto-refresh 60s.
 * Null-safe: κάθε πεδίο ελέγχεται πριν από τη χρήση.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  RefreshCw,
  Search,
  Check,
  X,
  TrendingUp,
  Calendar,
  Hourglass,
  XCircle,
  Sparkles,
  CreditCard,
  Banknote,
  Wallet,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function readJwt() {
  return (
    localStorage.getItem("kf_jwt") ||
    localStorage.getItem("kiosk_jwt") ||
    localStorage.getItem("token") ||
    localStorage.getItem("jwt") ||
    ""
  );
}

// ── Null-safe formatters ───────────────────────────────────────────────────
function currency(n) {
  const v = Number(n ?? 0);
  return `€${v.toLocaleString("el-GR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("el-GR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

// ── Config ────────────────────────────────────────────────────────────────
const FILTERS = [
  { key: "all",       label: "Όλες",         emoji: "📋" },
  { key: "pending",   label: "Εκκρεμείς",    emoji: "⏳" },
  { key: "completed", label: "Ολοκληρωμένες", emoji: "✅" },
  { key: "failed",    label: "Απέτυχαν",     emoji: "❌" },
  { key: "today",     label: "Σήμερα",       emoji: "📅" },
];

const STATUS_META = {
  completed:      { label: "ΟΛΟΚΛΗΡΩΘΗΚΕ", cls: "bg-emerald-500/20 text-emerald-300 ring-emerald-400/40" },
  pending_admin:  { label: "ΕΚΚΡΕΜΕΙ",     cls: "bg-amber-500/20 text-amber-300 ring-amber-400/40" },
  failed:         { label: "ΑΠΕΤΥΧΕ",      cls: "bg-rose-500/20 text-rose-300 ring-rose-400/40" },
};

const METHOD_META = {
  paypal: { label: "PayPal",     icon: CreditCard, tone: "text-sky-300" },
  bank:   { label: "Τραπεζική",  icon: Banknote,   tone: "text-lime-300" },
  cash:   { label: "Μετρητά",    icon: Wallet,     tone: "text-fuchsia-300" },
};

const CARDS = [
  { key: "revenue_month",         label: "Έσοδα Μήνα",         emoji: "💰", icon: TrendingUp, accent: "from-lime-500 to-emerald-600",   ring: "ring-lime-400/40",    kind: "eur" },
  { key: "revenue_today",         label: "Έσοδα Σήμερα",       emoji: "📅", icon: Calendar,   accent: "from-teal-500 to-emerald-600",   ring: "ring-teal-400/40",    kind: "eur" },
  { key: "pending_payments",      label: "Εκκρεμείς Πληρωμές", emoji: "⏳", icon: Hourglass,  accent: "from-amber-500 to-orange-600",   ring: "ring-amber-400/40",   kind: "int" },
  { key: "expired_subscriptions", label: "Ληγμένες Συνδρομές", emoji: "❌", icon: XCircle,    accent: "from-rose-500 to-red-600",       ring: "ring-rose-400/40",    kind: "int" },
  { key: "new_payments_24h",      label: "Νέες Πληρωμές 24h",  emoji: "✨", icon: Sparkles,   accent: "from-fuchsia-500 to-purple-600", ring: "ring-fuchsia-400/40", kind: "int" },
];

// ── Cards ─────────────────────────────────────────────────────────────────
function StatCard({ card, value, loading }) {
  const Icon = card.icon;
  const raw = value ?? (card.kind === "eur" ? 0 : 0);
  const display = card.kind === "eur" ? currency(raw) : raw;
  return (
    <div
      data-testid={`payments-stat-${card.key}`}
      className={`relative overflow-hidden rounded-2xl border border-white/5 bg-gradient-to-br ${card.accent} p-5 text-white shadow-xl ring-1 ${card.ring} transition hover:scale-[1.015]`}
    >
      <div className="absolute -top-6 -right-6 h-24 w-24 rounded-full bg-white/10 blur-2xl" />
      <div className="relative flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-white/80">
            <span className="mr-1">{card.emoji}</span>
            {card.label}
          </p>
          <p
            className={`mt-3 font-black tracking-tight ${
              card.kind === "eur" ? "text-3xl sm:text-4xl" : "text-4xl sm:text-5xl"
            }`}
            data-testid={`payments-stat-value-${card.key}`}
          >
            {loading ? (
              <span className="inline-block h-9 w-24 animate-pulse rounded bg-white/25" />
            ) : display}
          </p>
        </div>
        <div className="rounded-xl bg-white/15 p-2.5 backdrop-blur-sm">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

// ── Confirm modal ─────────────────────────────────────────────────────────
function ConfirmModal({ open, onClose, title, description, tone, confirmLabel,
                       onConfirm, showReason, showDays }) {
  const [reason, setReason] = useState("");
  const [days, setDays] = useState("30");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setReason(""); setDays("30"); } }, [open]);
  if (!open) return null;
  const toneCls = tone === "danger"
    ? "from-rose-500 to-red-600 text-white"
    : "from-emerald-500 to-lime-500 text-slate-950";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="payment-confirm-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl border border-white/10 bg-slate-900 p-5 shadow-2xl"
      >
        <h3 className="text-base font-black text-white">{title}</h3>
        <p className="mt-2 text-sm text-slate-300">{description}</p>
        {showDays && (
          <div className="mt-4">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-slate-400">
              Διάρκεια συνδρομής (ημέρες)
            </label>
            <input
              type="number" value={days} min={1}
              onChange={(e) => setDays(e.target.value)}
              className="w-full rounded-lg border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white outline-none focus:border-emerald-400"
              data-testid="payment-approve-days"
            />
          </div>
        )}
        {showReason && (
          <div className="mt-4">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-slate-400">
              Λόγος απόρριψης (προαιρετικό)
            </label>
            <textarea
              rows={3} value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full resize-none rounded-lg border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white outline-none focus:border-rose-400"
              data-testid="payment-reject-reason"
            />
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button" onClick={onClose}
            className="rounded-lg border border-white/10 px-4 py-2 text-xs font-semibold text-slate-300 hover:bg-white/5"
          >
            Ακύρωση
          </button>
          <button
            type="button"
            data-testid="payment-confirm-submit"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const payload = {};
                if (showDays) payload.duration_days = parseInt(days, 10) || 30;
                if (showReason) payload.reason = reason;
                await onConfirm(payload);
                onClose();
              } finally { setBusy(false); }
            }}
            className={`rounded-lg bg-gradient-to-br px-4 py-2 text-xs font-black shadow-lg transition hover:brightness-110 disabled:opacity-50 ${toneCls}`}
          >
            {busy ? "…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Method chip ───────────────────────────────────────────────────────────
function MethodChip({ method }) {
  const meta = METHOD_META[method] || METHOD_META.bank;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-lg bg-white/5 px-2 py-1 text-[11px] font-semibold ${meta.tone}`}
      data-testid={`payment-method-${method}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {meta.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending_admin;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ring-1 ${meta.cls}`}
      data-testid={`payment-status-${status}`}
    >
      {meta.label}
    </span>
  );
}

// ── Main tab ──────────────────────────────────────────────────────────────
export default function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [confirm, setConfirm] = useState({ open: false });
  const [lastFetched, setLastFetched] = useState(null);

  const client = useMemo(
    () => axios.create({
      baseURL: `${BACKEND_URL}/api`,
      headers: { Authorization: `Bearer ${readJwt()}` },
      timeout: 15_000,
    }),
    [],
  );

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [pRes, sRes] = await Promise.all([
        client.get("/admin/payments", { params: { status: filter, q: q || undefined } }),
        client.get("/admin/stats"),
      ]);
      setPayments(Array.isArray(pRes?.data?.payments) ? pRes.data.payments : []);
      setStats(sRes?.data && typeof sRes.data === "object" ? sRes.data : {});
      setLastFetched(new Date());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Αποτυχία φόρτωσης πληρωμών");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [client, filter, q]);

  useEffect(() => { load(false); }, [load]);
  useEffect(() => {
    const t = setInterval(() => load(true), 60_000);
    return () => clearInterval(t);
  }, [load]);

  async function fireAction(payment, verb, payload, successMsg) {
    try {
      await client.post(`/admin/payments/${payment.id}/${verb}`, payload || {});
      toast.success(successMsg);
      await load(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Αποτυχία ενέργειας");
    }
  }

  return (
    <div className="space-y-6" data-testid="cpanel-payments-tab">
      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5" data-testid="payments-stat-grid">
        {CARDS.map((card) => (
          <StatCard
            key={card.key}
            card={card}
            loading={loading}
            value={stats?.[card.key]}
          />
        ))}
      </div>

      {/* Header controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-1 items-center gap-2 rounded-xl border border-white/10 bg-slate-900/70 px-3 py-2 backdrop-blur">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Αναζήτηση · περίπτερο · reference · payment id"
            className="flex-1 bg-transparent text-sm text-white placeholder:text-slate-500 outline-none"
            data-testid="payments-search"
          />
        </div>
        <button
          type="button" onClick={() => load(false)}
          className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-slate-200 hover:bg-white/10"
          data-testid="payments-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Ανανέωση
        </button>
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap gap-2" data-testid="payments-filter-group">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            data-testid={`payments-filter-${f.key}`}
            className={`rounded-full px-3 py-1.5 text-xs font-bold transition ${
              filter === f.key
                ? "bg-emerald-500 text-slate-950 shadow-lg"
                : "border border-white/10 bg-white/5 text-slate-300 hover:bg-white/10"
            }`}
          >
            <span className="mr-1">{f.emoji}</span>{f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-400" data-testid="payments-loading">
            Φόρτωση…
          </div>
        ) : payments.length === 0 ? (
          <div className="p-10 text-center" data-testid="payments-empty">
            <div className="text-base font-semibold text-white">Δεν βρέθηκαν πληρωμές</div>
            <div className="text-xs text-slate-400 mt-1">
              No payments match the current filter · Δοκίμασε άλλο φίλτρο.
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payments-table">
              <thead className="bg-white/5 text-[10px] uppercase tracking-widest text-slate-400">
                <tr>
                  <th className="text-left px-4 py-3">ID Πληρωμής</th>
                  <th className="text-left px-4 py-3">Περίπτερο</th>
                  <th className="text-left px-4 py-3">Ποσό</th>
                  <th className="text-left px-4 py-3">Ημερομηνία</th>
                  <th className="text-left px-4 py-3">Μέθοδος</th>
                  <th className="text-left px-4 py-3">Κατάσταση</th>
                  <th className="text-right px-4 py-3">Ενέργειες</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr
                    key={p.id}
                    data-testid={`payment-row-${p.id}`}
                    className="border-t border-white/5 transition hover:bg-white/[0.03]"
                  >
                    <td className="px-4 py-3">
                      <div className="font-mono text-[11px] text-slate-300">{p.id}</div>
                      {p.reference && (
                        <div className="text-[10px] text-slate-500">{p.reference}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-bold text-white">{p.kiosk_name || "—"}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{p.tenant_id}</div>
                    </td>
                    <td className="px-4 py-3 font-bold text-white">
                      {currency(p.amount)}
                    </td>
                    <td className="px-4 py-3 text-slate-300">{formatDate(p.created_at)}</td>
                    <td className="px-4 py-3">
                      <MethodChip method={p.method} />
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={p.status} />
                      {p.status === "completed" && p.approved_by && (
                        <div className="text-[10px] text-slate-500 mt-0.5">by {p.approved_by}</div>
                      )}
                      {p.status === "failed" && p.rejected_reason && (
                        <div className="text-[10px] text-slate-500 mt-0.5 max-w-[200px] truncate">
                          {p.rejected_reason}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        {p.status === "pending_admin" ? (
                          <>
                            <button
                              type="button"
                              data-testid={`payment-approve-${p.id}`}
                              onClick={() => setConfirm({
                                open: true,
                                title: `Έγκριση πληρωμής · ${currency(p.amount)}`,
                                description: `Θα ενεργοποιηθεί η συνδρομή του ${p.kiosk_name || p.tenant_id} για τις επόμενες ημέρες που θα ορίσεις.`,
                                tone: "primary",
                                confirmLabel: "✔️ Έγκριση",
                                showDays: true,
                                onConfirm: (payload) => fireAction(
                                  p, "approve", payload,
                                  `Η πληρωμή ${p.id} εγκρίθηκε.`,
                                ),
                              })}
                              className="inline-flex items-center gap-1 rounded-lg border border-emerald-400/40 bg-emerald-500/15 px-2.5 py-1 text-[11px] font-bold text-emerald-200 transition hover:bg-emerald-500/25"
                            >
                              <Check className="h-3.5 w-3.5" />
                              Έγκριση
                            </button>
                            <button
                              type="button"
                              data-testid={`payment-reject-${p.id}`}
                              onClick={() => setConfirm({
                                open: true,
                                title: `Απόρριψη πληρωμής · ${currency(p.amount)}`,
                                description: "Η πληρωμή θα σημειωθεί ως failed και δεν θα επεκταθεί καμία συνδρομή.",
                                tone: "danger",
                                confirmLabel: "❌ Απόρριψη",
                                showReason: true,
                                onConfirm: (payload) => fireAction(
                                  p, "reject", payload,
                                  `Η πληρωμή ${p.id} απορρίφθηκε.`,
                                ),
                              })}
                              className="inline-flex items-center gap-1 rounded-lg border border-rose-400/40 bg-rose-500/15 px-2.5 py-1 text-[11px] font-bold text-rose-200 transition hover:bg-rose-500/25"
                            >
                              <X className="h-3.5 w-3.5" />
                              Απόρριψη
                            </button>
                          </>
                        ) : (
                          <span className="text-[10px] text-slate-500 italic">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="text-right text-[11px] text-slate-500" data-testid="payments-last-fetched">
        {lastFetched && `Τελευταία ενημέρωση · ${lastFetched.toLocaleTimeString("el-GR", {
          hour: "2-digit", minute: "2-digit", second: "2-digit",
        })} · auto-refresh 60δ`}
      </div>

      <ConfirmModal
        open={confirm.open}
        onClose={() => setConfirm({ open: false })}
        {...confirm}
      />
    </div>
  );
}
